import React, { useState } from 'react';
import { Search, MapPin, Navigation } from 'lucide-react';

interface Location {
  id: string;
  name: string;
  lat: number;
  lon: number;
  status: 'active' | 'pending' | 'delivered' | 'cancelled';
  type?: 'order' | 'driver' | 'warehouse' | 'customer';
  address?: string;
}

interface LocationListProps {
  locations: Location[];
  onLocationClick?: (location: Location) => void;
  onNavigateTo?: (location: Location) => void;
}

const LocationList: React.FC<LocationListProps> = ({
  locations,
  onLocationClick,
  onNavigateTo,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  const filteredLocations = locations.filter((loc) => {
    const matchesSearch =
      loc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      loc.address?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || loc.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active':
        return 'กำลังดำเนินการ';
      case 'pending':
        return 'รอดำเนินการ';
      case 'delivered':
        return 'ส่งสำเร็จ';
      case 'cancelled':
        return 'ยกเลิก';
      default:
        return status;
    }
  };

  return (
    <div className="bg-white rounded-lg shadow border border-gray-200 h-full flex flex-col">
      <div className="p-4 border-b border-gray-200">
        <h3 className="font-semibold text-gray-900 mb-3">รายการตำแหน่ง</h3>
        
        {/* Search */}
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="ค้นหาตำแหน่ง..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
          />
        </div>

        {/* Filter */}
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
        >
          <option value="all">สถานะทั้งหมด</option>
          <option value="active">กำลังดำเนินการ</option>
          <option value="pending">รอดำเนินการ</option>
          <option value="delivered">ส่งสำเร็จ</option>
          <option value="cancelled">ยกเลิก</option>
        </select>
      </div>

      {/* Location List */}
      <div className="flex-1 overflow-y-auto">
        {filteredLocations.length === 0 ? (
          <div className="p-4 text-center text-gray-500 text-sm">
            ไม่พบตำแหน่ง
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredLocations.map((location) => (
              <div
                key={location.id}
                className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                onClick={() => onLocationClick?.(location)}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2 flex-1">
                    <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">{location.name}</h4>
                      {location.address && (
                        <p className="text-xs text-gray-500 mt-1">{location.address}</p>
                      )}
                    </div>
                  </div>
                  <span
                    className={`px-2 py-1 text-xs rounded-full ${getStatusColor(location.status)}`}
                  >
                    {getStatusLabel(location.status)}
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>Lat: {location.lat.toFixed(6)}, Lon: {location.lon.toFixed(6)}</span>
                  {onNavigateTo && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onNavigateTo(location);
                      }}
                      className="flex items-center gap-1 text-blue-600 hover:text-blue-800"
                    >
                      <Navigation className="w-3 h-3" />
                      นำทาง
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default LocationList;
